import secureApi from "../../utils/secureApi";

export type HighlightAccessType = "free" | "paid";

export interface ExistingMatchHighlight {
  default?: string | null;
  thumbnail?: string | null;
  uploadedDate?: string | null;
  accessType?: HighlightAccessType;
}

export interface ExistingPlayerHighlight {
  player: string | { _id: string; name?: string; profilePic?: string | null };
  highlights: string;
  thumbnail?: string | null;
}

export interface ExistingTeamHighlight {
  team: string | { _id: string; name?: string; shortName?: string; logo?: string | null };
  highlights: string;
  thumbnail?: string | null;
}

export interface MatchHighlightsResponse {
  matchId: string;
  matchHighlights: ExistingMatchHighlight | null;
  playerHighlights: ExistingPlayerHighlight[];
  teamHighlights: ExistingTeamHighlight[];
}

export interface MatchHighlightsPayload {
  matchHighlights?: {
    default?: string | null;
    thumbnail?: string | null;
    uploadedDate?: string | null;
    accessType?: HighlightAccessType;
  };
  playerHighlights?: Array<{
    player: string;
    highlights: string;
    thumbnail?: string | null;
  }>;
  teamHighlights?: Array<{
    team: string;
    highlights: string;
    thumbnail?: string | null;
  }>;
}

interface PresignedUploadResponse {
  presignedUrl: string;
  key: string;
}

export type HighlightKind = "match" | "player" | "team";
export type HighlightAsset = "video" | "thumbnail";

export interface HighlightUploadOptions {
  file: File;
  tournamentId: string;
  seasonId: string;
  matchId: string;
  sequence: string;
  kind: HighlightKind;
  asset: HighlightAsset;
}

const unwrap = <T,>(response: any): T => {
  if (response?.data?.data) return response.data.data as T;
  if (response?.data) return response.data as T;
  return response as T;
};

const endpoint = (matchId: string, sport: string) =>
  `/api/v2/tournaments/manage/match-highlights/${matchId}?sport=${encodeURIComponent(sport)}`;

export async function getMatchHighlights(matchId: string, sport: string): Promise<MatchHighlightsResponse> {
  const data = unwrap<MatchHighlightsResponse>(await secureApi.get(endpoint(matchId, sport)));
  return {
    matchId: data.matchId || matchId,
    matchHighlights: data.matchHighlights || null,
    playerHighlights: Array.isArray(data.playerHighlights) ? data.playerHighlights : [],
    teamHighlights: Array.isArray(data.teamHighlights) ? data.teamHighlights : [],
  };
}

export async function updateMatchHighlights(
  matchId: string,
  sport: string,
  payload: MatchHighlightsPayload
): Promise<MatchHighlightsResponse> {
  return unwrap<MatchHighlightsResponse>(await secureApi.patch(endpoint(matchId, sport), payload));
}

const highlightContextNames: Record<HighlightKind, string> = {
  match: "match-highlights",
  player: "player-highlights",
  team: "team-highlights",
};

const thumbnailExtensions: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

export const createHighlightAssetSequence = () => {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  const values = new Uint8Array(8);

  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(values);
  } else {
    for (let index = 0; index < values.length; index += 1) {
      values[index] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(values, (value) => alphabet[value % alphabet.length]).join("");
};

export const getHighlightAssetSequence = (value?: string | null) => {
  const fileName = value?.split(/[?#]/)[0].split("/").pop();
  const match = fileName?.match(/^([a-z0-9]{8})(?:_thumbnail)?\.(?:mp4|jpe?g|png|webp)$/i);
  return match?.[1]?.toLowerCase() || "";
};

const fileNameFor = (file: File, sequence: string, asset: HighlightAsset) => {
  if (asset === "video") {
    if (file.type !== "video/mp4") {
      throw new Error("Please select an MP4 video file.");
    }
    return `${sequence}.mp4`;
  }

  const extension = thumbnailExtensions[file.type];
  if (!extension) {
    throw new Error("Please select a JPG, PNG, or WebP thumbnail.");
  }
  return `${sequence}_thumbnail.${extension}`;
};

export async function requestHighlightUploadUrl({
  file,
  tournamentId,
  seasonId,
  matchId,
  sequence,
  kind,
  asset,
}: HighlightUploadOptions): Promise<PresignedUploadResponse> {
  const context = `highlights/${highlightContextNames[kind]}/${tournamentId}/${seasonId}/${matchId}`;
  const fileName = fileNameFor(file, sequence, asset);

  const data = unwrap<PresignedUploadResponse>(
    await secureApi.post("/api/v1/general/presigned-url", {
      fileName,
      contentType: file.type,
      context,
    })
  );

  if (!data.presignedUrl || !data.key) {
    throw new Error("The upload service did not return a presigned URL.");
  }

  return data;
}

export function putPresignedFile(
  presignedUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress?.(Math.round((event.loaded / event.total) * 100));
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`File upload failed with status ${request.status}.`));
      }
    };

    request.onerror = () => reject(new Error("File upload failed."));
    request.open("PUT", presignedUrl);
    request.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    request.send(file);
  });
}

export async function uploadHighlightAsset(
  options: HighlightUploadOptions,
  onProgress?: (percent: number) => void
): Promise<string> {
  const upload = await requestHighlightUploadUrl(options);
  await putPresignedFile(upload.presignedUrl, options.file, onProgress);
  return upload.key;
}
